/**
 * useRealtime — React hook for the HornDeliver Socket.io realtime service.
 *
 * Connects to the realtime mini-service (port 3003) via the Caddy gateway.
 * Authenticates using the session cookie (the socket.io middleware verifies
 * it server-side). Provides:
 *   - connection status
 *   - room join/leave helpers
 *   - typed event subscription
 *   - courier location broadcasting (for courier clients)
 *
 * Usage:
 *   const { isConnected, joinJob, onJobStatus } = useRealtime();
 *   useEffect(() => {
 *     joinJob(jobId);
 *     const off = onJobStatus((data) => { ... });
 *     return off;
 *   }, [jobId]);
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { io, type Socket } from "socket.io-client";

const REALTIME_PORT = 3003;

type EventHandler = (data: any) => void;

export interface RealtimeHook {
  isConnected: boolean;
  socket: Socket | null;
  joinJob: (jobId: string) => void;
  leaveJob: (jobId: string) => void;
  joinAdmin: () => void;
  sendLocation: (data: { jobId: string; lat: number; lng: number; heading?: number; speed?: number }) => void;
  sendTyping: (jobId: string, isTyping: boolean) => void;
  on: (event: string, handler: EventHandler) => () => void;
  // Convenience typed event helpers
  onJobStatus: (handler: (data: JobStatusEvent) => void) => () => void;
  onJobLocation: (handler: (data: JobLocationEvent) => void) => () => void;
  onChatMessage: (handler: (data: ChatMessageEvent) => void) => () => void;
  onNotification: (handler: (data: NotificationEvent) => void) => () => void;
  onChatTyping: (handler: (data: ChatTypingEvent) => void) => () => void;
  onReviewReceived: (handler: (data: ReviewEvent) => void) => () => void;
}

export interface JobStatusEvent {
  jobId: string;
  jobCode: string;
  status: string;
  fromStatus: string;
  customerId: string;
  providerId: string | null;
  timestamp: string;
}

export interface JobLocationEvent {
  jobId: string;
  lat: number;
  lng: number;
  heading?: number;
  speed?: number;
  timestamp: string;
}

export interface ChatMessageEvent {
  id: string;
  jobId: string;
  senderId: string;
  receiverId: string;
  content: string;
  type: string;
  createdAt: string;
  senderName?: string;
  sender?: { id: string; name: string };
}

export interface NotificationEvent {
  id: string;
  type: string;
  title: string;
  body: string;
  data: string | null;
  createdAt: string;
}

export interface ChatTypingEvent {
  jobId: string;
  userId: string;
  isTyping: boolean;
}

export interface ReviewEvent {
  reviewId: string;
  jobId: string;
  jobCode: string;
  rating: number;
  targetType: string;
  reviewerName: string;
}

let socketInstance: Socket | null = null;

export function useRealtime(): RealtimeHook {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (socketInstance) {
      socketRef.current = socketInstance;
      // Defer the initial state sync to avoid cascading renders.
      Promise.resolve().then(() => setIsConnected(socketInstance!.connected));
      // Re-wire the connect/disconnect listeners for this hook instance
      const onConnect = () => setIsConnected(true);
      const onDisconnect = () => setIsConnected(false);
      socketInstance.on("connect", onConnect);
      socketInstance.on("disconnect", onDisconnect);
      return () => {
        socketInstance?.off("connect", onConnect);
        socketInstance?.off("disconnect", onDisconnect);
      };
    }

    // Connect to the realtime service via the Caddy gateway.
    // The socket.io path is "/socket.io/" and XTransformPort MUST be in
    // the query so Caddy forwards to port 3003.
    const sock = io("/", {
      path: "/socket.io/",
      query: { XTransformPort: "3003" },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
      withCredentials: true, // send the session cookie
    });
    socketInstance = sock;
    socketRef.current = sock;

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    sock.on("connect", onConnect);
    sock.on("disconnect", onDisconnect);
    sock.on("connect_error", (err: any) => {
      // Silent — realtime is best-effort. The app works without it.
      console.debug("[realtime] connect error:", err?.message);
    });

    return () => {
      sock.off("connect", onConnect);
      sock.off("disconnect", onDisconnect);
      // Don't disconnect on unmount — keep the singleton alive across
      // route changes so the notification channel stays open. The socket
      // is only disconnected on full page unload.
    };
  }, []);

  const joinJob = useCallback((jobId: string) => {
    socketRef.current?.emit("job:join", jobId);
  }, []);

  const leaveJob = useCallback((jobId: string) => {
    socketRef.current?.emit("job:leave", jobId);
  }, []);

  const joinAdmin = useCallback(() => {
    socketRef.current?.emit("admin:join");
  }, []);

  const sendLocation = useCallback(
    (data: { jobId: string; lat: number; lng: number; heading?: number; speed?: number }) => {
      socketRef.current?.emit("courier:location", data);
    },
    []
  );

  const sendTyping = useCallback((jobId: string, isTyping: boolean) => {
    socketRef.current?.emit("chat:typing", { jobId, isTyping });
  }, []);

  const on = useCallback((event: string, handler: EventHandler) => {
    const sock = socketRef.current;
    if (!sock) return () => {};
    sock.on(event, handler);
    return () => {
      sock.off(event, handler);
    };
  }, []);

  const onJobStatus = useCallback(
    (handler: (data: JobStatusEvent) => void) => on("job:status", handler as EventHandler),
    [on]
  );
  const onJobLocation = useCallback(
    (handler: (data: JobLocationEvent) => void) => on("job:location", handler as EventHandler),
    [on]
  );
  const onChatMessage = useCallback(
    (handler: (data: ChatMessageEvent) => void) => on("chat:message", handler as EventHandler),
    [on]
  );
  const onNotification = useCallback(
    (handler: (data: NotificationEvent) => void) => on("notification", handler as EventHandler),
    [on]
  );
  const onChatTyping = useCallback(
    (handler: (data: ChatTypingEvent) => void) => on("chat:typing", handler as EventHandler),
    [on]
  );
  const onReviewReceived = useCallback(
    (handler: (data: ReviewEvent) => void) => on("review:received", handler as EventHandler),
    [on]
  );

  return {
    isConnected,
    socket: socketInstance,
    joinJob,
    leaveJob,
    joinAdmin,
    sendLocation,
    sendTyping,
    on,
    onJobStatus,
    onJobLocation,
    onChatMessage,
    onNotification,
    onChatTyping,
    onReviewReceived,
  };
}
